<template>
  <admin-template>
    <div class="min-h-screen bg-gray-50 font-sans antialiased text-gray-900">

      <!-- TOPBAR -->
      <header class="sticky top-0 z-40 bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div class="max-w-7xl mx-auto flex items-center justify-between gap-4">

          <div class="flex items-center gap-4">
            <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center font-black text-white text-sm shadow-md shadow-orange-500/30">T</div>
            <div>
              <h1 class="font-black text-gray-900 text-lg leading-none">Blog</h1>
              <p class="text-gray-400 text-xs font-medium mt-0.5">Administration</p>
            </div>
          </div>

          <!-- Stats rapides -->
          <div class="hidden md:flex items-center">
            <div class="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-2xl px-5 py-2.5">
              <!-- Total — cliquable pour reset le filtre -->
              <button @click="setFilter('all')" class="text-center group">
                <p :class="['text-xl font-black leading-none transition-colors', filter === 'all' ? 'text-orange-500' : 'text-gray-900 group-hover:text-orange-500']">
                  {{ stats.total }}
                </p>
                <p class="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Total</p>
              </button>
              <div class="w-px h-8 bg-gray-200"></div>
              <!-- Publiés -->
              <button @click="setFilter('published')" class="text-center group">
                <p :class="['text-xl font-black leading-none transition-colors', filter === 'published' ? 'text-green-500' : 'text-green-600 group-hover:text-green-500']">
                  {{ stats.published }}
                </p>
                <p class="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Publiés</p>
              </button>
              <div class="w-px h-8 bg-gray-200"></div>
              <!-- Brouillons -->
              <button @click="setFilter('draft')" class="text-center group">
                <p :class="['text-xl font-black leading-none transition-colors', filter === 'draft' ? 'text-amber-400' : 'text-amber-500 group-hover:text-amber-400']">
                  {{ stats.drafts }}
                </p>
                <p class="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">Brouillons</p>
              </button>
            </div>
          </div>

          <button
            @click="openCreate"
            class="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-black text-sm transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-500/25"
          >
            <i class="fa-solid fa-plus"></i>
            <span class="hidden sm:inline">Nouvel article</span>
          </button>
        </div>
      </header>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 py-8">

        <!-- FILTERS BAR -->
        <div class="flex flex-wrap gap-3 mb-6">

          <!-- Search -->
          <div class="relative flex-1 min-w-[200px]">
            <i class="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
            <input
              v-model="search"
              @input="onSearch"
              type="search"
              placeholder="Rechercher un article…"
              class="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all shadow-sm"
            />
          </div>

          <!-- Status tabs -->
          <div class="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
            <button
              v-for="f in filters" :key="f.value"
              @click="setFilter(f.value)"
              :class="[
                'px-4 py-1.5 rounded-lg text-xs font-black transition-all flex items-center gap-1.5',
                filter === f.value
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              ]"
            >
              <span
                v-if="f.dot"
                :class="['w-1.5 h-1.5 rounded-full flex-shrink-0', filter === f.value ? 'bg-white' : f.dot]"
              ></span>
              {{ f.label }}
              <!-- Badge compteur -->
              <span
                v-if="filterCount(f.value) !== null"
                :class="[
                  'ml-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-black',
                  filter === f.value ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                ]"
              >{{ filterCount(f.value) }}</span>
            </button>
          </div>

          <!-- Sort -->
          <select
            v-model="sort"
            @change="onSortChange"
            class="bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all shadow-sm font-medium"
          >
            <option value="recent">Plus récents</option>
            <option value="popular">Plus populaires</option>
            <option value="alpha">A → Z</option>
          </select>

          <!-- Reset actif si filtres en cours -->
          <button
            v-if="hasActiveFilters"
            @click="resetFilters"
            class="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-orange-200 bg-orange-50 text-orange-600 text-xs font-black hover:bg-orange-100 transition-all"
            title="Réinitialiser les filtres"
          >
            <i class="fa-solid fa-xmark"></i>
            Réinitialiser
          </button>
        </div>

        <!-- Résumé des filtres actifs -->
        <div v-if="hasActiveFilters" class="flex flex-wrap items-center gap-2 mb-5">
          <span class="text-xs text-gray-400 font-bold uppercase tracking-wider">Filtres actifs :</span>

          <span v-if="search" class="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
            <i class="fa-solid fa-magnifying-glass text-gray-400 text-[10px]"></i>
            « {{ search }} »
            <button @click="clearSearch" class="ml-0.5 text-gray-400 hover:text-red-500 transition-colors">
              <i class="fa-solid fa-xmark text-[10px]"></i>
            </button>
          </span>

          <span v-if="filter !== 'all'" class="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
            <span :class="['w-1.5 h-1.5 rounded-full', filter === 'published' ? 'bg-green-500' : 'bg-amber-400']"></span>
            {{ filter === 'published' ? 'Publiés' : 'Brouillons' }}
            <button @click="setFilter('all')" class="ml-0.5 text-gray-400 hover:text-red-500 transition-colors">
              <i class="fa-solid fa-xmark text-[10px]"></i>
            </button>
          </span>

          <span v-if="sort !== 'recent'" class="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
            <i class="fa-solid fa-arrow-down-wide-short text-gray-400 text-[10px]"></i>
            {{ sortLabels[sort] }}
            <button @click="clearSort" class="ml-0.5 text-gray-400 hover:text-red-500 transition-colors">
              <i class="fa-solid fa-xmark text-[10px]"></i>
            </button>
          </span>

          <span class="text-xs text-gray-400 font-medium ml-2">
            — {{ pagination.total }} résultat{{ pagination.total > 1 ? 's' : '' }}
          </span>
        </div>

        <!-- TABLE CARD -->
        <div class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

          <!-- Table header -->
          <div
            class="grid items-center px-5 py-3 border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-widest text-gray-400"
            style="grid-template-columns: 56px 1fr 130px 90px 90px 180px"
          >
            <div></div>
            <div>Article</div>
            <div class="hidden lg:block">Statut</div>
            <div class="hidden md:block">Vues</div>
            <div class="hidden md:block">Date</div>
            <div class="text-right">Actions</div>
          </div>

          <!-- Skeleton -->
          <div v-if="loading">
            <div
              v-for="i in 7" :key="i"
              class="grid items-center px-5 py-4 border-b border-gray-100 last:border-0 animate-pulse"
              style="grid-template-columns: 56px 1fr 130px 90px 90px 180px"
            >
              <div class="w-10 h-10 rounded-xl bg-gray-100"></div>
              <div class="space-y-2 pr-4">
                <div class="h-3.5 bg-gray-100 rounded-full w-3/4"></div>
                <div class="h-2.5 bg-gray-100 rounded-full w-1/2"></div>
              </div>
              <div class="hidden lg:block h-6 w-24 bg-gray-100 rounded-full"></div>
              <div class="hidden md:block h-3 w-10 bg-gray-100 rounded-full"></div>
              <div class="hidden md:block h-3 w-16 bg-gray-100 rounded-full"></div>
              <div class="flex gap-2 justify-end">
                <div class="w-8 h-8 rounded-lg bg-gray-100"></div>
                <div class="w-8 h-8 rounded-lg bg-gray-100"></div>
                <div class="w-20 h-8 rounded-lg bg-gray-100"></div>
              </div>
            </div>
          </div>

          <!-- Empty -->
          <div v-else-if="articles.length === 0" class="py-20 text-center">
            <div class="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl text-orange-300">
              <i class="fa-solid fa-newspaper"></i>
            </div>
            <p class="text-gray-700 font-black mb-1">Aucun article trouvé</p>
            <p class="text-gray-400 text-sm mb-6">
              <template v-if="hasActiveFilters">Aucun résultat pour ces filtres.</template>
              <template v-else>Créez votre premier article !</template>
            </p>
            <div class="flex items-center justify-center gap-3">
              <button v-if="hasActiveFilters" @click="resetFilters" class="border border-gray-200 text-gray-600 px-5 py-2.5 rounded-xl font-black text-sm hover:bg-gray-50 transition-colors">
                <i class="fa-solid fa-xmark mr-2"></i>Réinitialiser les filtres
              </button>
              <button v-else @click="openCreate" class="bg-orange-500 text-white px-6 py-2.5 rounded-xl font-black text-sm hover:bg-orange-600 transition-colors shadow-md shadow-orange-500/20">
                <i class="fa-solid fa-plus mr-2"></i>Créer un article
              </button>
            </div>
          </div>

          <!-- Rows -->
          <div v-else>
            <div
              v-for="article in articles" :key="article.id"
              class="group grid items-center px-5 py-3.5 border-b border-gray-100 last:border-0 hover:bg-orange-50/40 transition-colors"
              style="grid-template-columns: 56px 1fr 130px 90px 90px 180px"
            >
              <!-- Thumbnail -->
              <div class="w-11 h-11 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-100">
                <img v-if="article.image_url" :src="article.image_url" :alt="article.title" class="w-full h-full object-cover" />
                <div v-else class="w-full h-full flex items-center justify-center text-gray-300 text-lg">
                  <i class="fa-solid fa-image"></i>
                </div>
              </div>

              <!-- Title -->
              <div class="min-w-0 pr-4">
                <p class="font-bold text-gray-900 truncate text-sm">{{ article.title }}</p>
                <p class="text-gray-400 text-xs mt-0.5 truncate font-mono">/blog/{{ article.slug }}</p>
              </div>

              <!-- Status badge -->
              <div class="hidden lg:block">
                <span :class="[
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black',
                  article.status === 'published'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                ]">
                  <span :class="['w-1.5 h-1.5 rounded-full flex-shrink-0', article.status === 'published' ? 'bg-green-500' : 'bg-amber-400']"></span>
                  {{ article.status === 'published' ? 'Publié' : 'Brouillon' }}
                </span>
              </div>

              <!-- Views -->
              <div class="hidden md:block text-sm text-gray-500 font-medium">
                <i class="fa-solid fa-eye text-gray-300 mr-1.5 text-xs"></i>{{ (article.views_count || 0).toLocaleString('fr-FR') }}
              </div>

              <!-- Date -->
              <div class="hidden md:block text-xs text-gray-400 font-medium">{{ formatDate(article.created_at) }}</div>

              <!-- Actions -->
              <div class="flex items-center gap-1.5 justify-end">
                <!-- Voir en ligne -->
                <a
                  :href="`/blog/${article.slug}`"
                  target="_blank"
                  class="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-all"
                  title="Voir l'article"
                >
                  <i class="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                </a>

                <!-- Modifier -->
                <button
                  @click="openEdit(article)"
                  class="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:border-orange-400 hover:bg-orange-50 flex items-center justify-center text-gray-400 hover:text-orange-600 transition-all"
                  title="Modifier"
                >
                  <i class="fa-solid fa-pen text-xs"></i>
                </button>

                <!-- Supprimer -->
                <button
                  @click="confirmDelete(article)"
                  class="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:border-red-400 hover:bg-red-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all"
                  title="Supprimer"
                >
                  <i class="fa-solid fa-trash text-xs"></i>
                </button>

                <!-- ★ Bouton rapide Publier / Dépublier -->
                <button
                  @click="quickToggle(article)"
                  :disabled="article._toggling"
                  :class="[
                    'flex items-center gap-1.5 px-3 h-8 rounded-lg border font-black text-xs transition-all disabled:opacity-60 whitespace-nowrap',
                    article.status === 'published'
                      ? 'border-gray-200 bg-white text-gray-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600'
                      : 'border-green-300 bg-green-50 text-green-700 hover:bg-green-500 hover:text-white hover:border-green-500 shadow-sm shadow-green-500/15'
                  ]"
                  :title="article.status === 'published' ? 'Dépublier' : 'Publier maintenant'"
                >
                  <i v-if="article._toggling" class="fa-solid fa-circle-notch fa-spin text-[10px]"></i>
                  <template v-else>
                    <i :class="article.status === 'published' ? 'fa-solid fa-eye-slash' : 'fa-solid fa-bolt'" class="text-[10px]"></i>
                    {{ article.status === 'published' ? 'Dépublier' : 'Publier' }}
                  </template>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.last_page > 1" class="mt-6 flex items-center justify-between">
          <p class="text-sm text-gray-400 font-medium">
            Page <span class="text-gray-700 font-bold">{{ page }}</span> / <span class="text-gray-700 font-bold">{{ pagination.last_page }}</span>
            &nbsp;·&nbsp; <span class="text-gray-700 font-bold">{{ pagination.total }}</span> articles
          </p>
          <div class="flex items-center gap-1.5">
            <button @click="goTo(page - 1)" :disabled="page === 1" class="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-400 hover:border-orange-400 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm">
              <i class="fa-solid fa-chevron-left text-xs"></i>
            </button>
            <div v-for="p in pages" :key="p">
              <span v-if="p === '...'" class="w-9 h-9 flex items-center justify-center text-gray-400 font-black">…</span>
              <button v-else @click="goTo(p)" :class="['w-9 h-9 rounded-xl font-black text-sm transition-all shadow-sm', p === page ? 'bg-orange-500 text-white border border-orange-500' : 'bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500']">{{ p }}</button>
            </div>
            <button @click="goTo(page + 1)" :disabled="page === pagination.last_page" class="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-400 hover:border-orange-400 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm">
              <i class="fa-solid fa-chevron-right text-xs"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- ═══════════════════ MODAL ÉDITEUR ═══════════════════ -->
      <Transition name="modal">
        <div v-if="modal.open" class="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto">
          <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" @click="closeModal"></div>

          <div class="relative w-full max-w-4xl bg-white rounded-3xl border border-gray-200 shadow-2xl z-10 mb-10">
            <!-- Header -->
            <div class="flex items-center justify-between px-8 py-6 border-b border-gray-100">
              <div>
                <h2 class="text-xl font-black text-gray-900">
                  {{ modal.mode === 'create' ? 'Nouvel article' : 'Modifier l\'article' }}
                </h2>
                <p class="text-gray-400 text-sm mt-0.5">{{ modal.mode === 'create' ? 'Remplissez les champs puis publiez.' : 'Apportez vos modifications et sauvegardez.' }}</p>
              </div>
              <button @click="closeModal" class="w-9 h-9 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-all">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>

            <!-- Form body -->
            <div class="p-8 space-y-6">
              <!-- Titre -->
              <div>
                <label class="block text-xs font-black uppercase tracking-wider text-gray-500 mb-2">Titre <span class="text-orange-500">*</span></label>
                <input
                  v-model="form.title"
                  @input="generateSlug"
                  type="text"
                  placeholder="Ex: Maintenance chaudière obligatoire en 2026…"
                  class="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all font-medium text-sm"
                />
              </div>

              <!-- Slug -->
              <div>
                <label class="block text-xs font-black uppercase tracking-wider text-gray-500 mb-2">
                  Slug URL
                  <span class="text-gray-400 font-medium normal-case tracking-normal ml-2">— généré depuis le titre</span>
                </label>
                <div class="relative flex items-center">
                  <span class="absolute left-4 text-gray-400 text-sm font-mono select-none">/blog/</span>
                  <input
                    v-model="form.slug"
                    type="text"
                    class="w-full pl-[72px] pr-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl text-gray-600 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all font-mono text-sm"
                  />
                </div>
              </div>

              <!-- Image URL -->
              <div>
                <label class="block text-xs font-black uppercase tracking-wider text-gray-500 mb-2">Image de couverture</label>
                <div class="flex gap-3 items-start">
                  <input
                    v-model="form.image_url"
                    type="url"
                    placeholder="https://images.unsplash.com/…"
                    class="flex-1 px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all text-sm"
                  />
                  <div class="w-16 h-16 rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0 flex items-center justify-center text-gray-300 text-2xl">
                    <img v-if="form.image_url" :src="form.image_url" class="w-full h-full object-cover" @error="e => e.target.style.display='none'" />
                    <i v-else class="fa-solid fa-image"></i>
                  </div>
                </div>
              </div>

              <!-- Statut -->
              <div>
                <label class="block text-xs font-black uppercase tracking-wider text-gray-500 mb-3">Statut de publication</label>
                <div class="flex gap-3">
                  <button
                    v-for="s in statusOptions" :key="s.value"
                    @click="form.status = s.value"
                    :class="[
                      'flex-1 flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-2xl border-2 transition-all font-bold text-sm',
                      form.status === s.value
                        ? s.value === 'published'
                          ? 'border-green-400 bg-green-50 text-green-700'
                          : 'border-amber-400 bg-amber-50 text-amber-700'
                        : 'border-gray-200 text-gray-400 hover:border-gray-300 bg-white'
                    ]"
                  >
                    <i :class="[s.icon, 'text-sm']"></i> {{ s.label }}
                  </button>
                </div>
              </div>

              <!-- Contenu -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="text-xs font-black uppercase tracking-wider text-gray-500">
                    Contenu HTML <span class="text-orange-500">*</span>
                  </label>
                  <div class="flex gap-1 bg-gray-100 rounded-xl p-1">
                    <button @click="contentView = 'code'" :class="['px-3 py-1.5 rounded-lg text-xs font-black transition-all', contentView === 'code' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-700']">
                      <i class="fa-solid fa-code mr-1"></i>HTML
                    </button>
                    <button @click="contentView = 'preview'" :class="['px-3 py-1.5 rounded-lg text-xs font-black transition-all', contentView === 'preview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-700']">
                      <i class="fa-solid fa-eye mr-1"></i>Aperçu
                    </button>
                  </div>
                </div>
                <div v-show="contentView === 'code'">
                  <textarea
                    v-model="form.content"
                    rows="16"
                    placeholder="<article>…</article>"
                    class="w-full px-5 py-4 bg-gray-950 border border-gray-200 rounded-2xl text-green-300 placeholder-gray-700 focus:outline-none focus:border-orange-400 transition-colors font-mono text-sm leading-relaxed resize-y"
                  ></textarea>
                </div>
                <div v-show="contentView === 'preview'" class="bg-white border border-gray-200 rounded-2xl p-8 min-h-[300px] max-h-[500px] overflow-y-auto">
                  <div class="blog-content" v-html="form.content || '<p style=\'color:#9ca3af;font-style:italic\'>Votre contenu apparaîtra ici…</p>'"></div>
                </div>
              </div>

              <!-- Meta info -->
              <div class="flex items-center gap-5 text-xs text-gray-400 font-medium pt-1">
                <span><i class="fa-solid fa-align-left text-gray-300 mr-1.5"></i>{{ wordCount }} mots</span>
                <span><i class="fa-regular fa-clock text-gray-300 mr-1.5"></i>~{{ readTimeEstimate }} min de lecture</span>
                <span class="ml-auto text-gray-300">{{ form.content.length.toLocaleString('fr-FR') }} caractères</span>
              </div>
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-between px-8 py-6 border-t border-gray-100 bg-gray-50 rounded-b-3xl">
              <button @click="closeModal" class="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-300 font-bold text-sm transition-all bg-white">
                Annuler
              </button>
              <div class="flex items-center gap-3">
                <p v-if="formError" class="text-red-500 text-xs font-bold">
                  <i class="fa-solid fa-circle-exclamation mr-1"></i>{{ formError }}
                </p>
                <button
                  @click="save"
                  :disabled="saving"
                  class="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-8 py-2.5 rounded-xl font-black text-sm transition-all hover:-translate-y-0.5 disabled:hover:translate-y-0 shadow-lg shadow-orange-500/25"
                >
                  <i v-if="saving" class="fa-solid fa-circle-notch fa-spin"></i>
                  <i v-else class="fa-solid fa-floppy-disk"></i>
                  {{ saving ? 'Enregistrement…' : (modal.mode === 'create' ? 'Créer l\'article' : 'Sauvegarder') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <!-- ═══════════════════ MODAL SUPPRESSION ═══════════════════ -->
      <Transition name="modal">
        <div v-if="deleteModal.open" class="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" @click="deleteModal.open = false"></div>
          <div class="relative w-full max-w-md bg-white rounded-3xl border border-gray-200 shadow-2xl z-10 p-8 text-center">
            <div class="w-16 h-16 bg-red-50 border border-red-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <i class="fa-solid fa-trash text-2xl text-red-400"></i>
            </div>
            <h3 class="text-xl font-black text-gray-900 mb-2">Supprimer cet article ?</h3>
            <p class="text-gray-600 text-sm mb-1 font-medium">« {{ deleteModal.article?.title }} »</p>
            <p class="text-gray-400 text-xs mb-8">Cette action est définitive et irréversible.</p>
            <div class="flex gap-3">
              <button @click="deleteModal.open = false" class="flex-1 py-3 rounded-2xl border border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-300 font-bold text-sm transition-all bg-white">
                Annuler
              </button>
              <button @click="deleteArticle" :disabled="deleting" class="flex-1 py-3 rounded-2xl bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-black text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-red-500/20">
                <i v-if="deleting" class="fa-solid fa-circle-notch fa-spin"></i>
                <i v-else class="fa-solid fa-trash"></i>
                {{ deleting ? 'Suppression…' : 'Supprimer définitivement' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- TOAST -->
      <Transition name="toast">
        <div v-if="toast.show" :class="[
          'fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-2xl shadow-2xl font-bold text-sm border',
          toast.type === 'success'
            ? 'bg-white border-green-200 text-green-800 shadow-green-500/10'
            : 'bg-white border-red-200 text-red-700 shadow-red-500/10'
        ]">
          <div :class="['w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0', toast.type === 'success' ? 'bg-green-100' : 'bg-red-100']">
            <i :class="['text-sm', toast.type === 'success' ? 'fa-solid fa-check text-green-600' : 'fa-solid fa-xmark text-red-500']"></i>
          </div>
          {{ toast.message }}
        </div>
      </Transition>

    </div>
  </admin-template>
</template>

<script>
export default {
  name: 'AdminBlog',

  data() {
    return {
      // Liste
      articles: [],
      loading: true,

      // Filtres — source de vérité unique
      search: '',
      filter: 'all',   // 'all' | 'published' | 'draft'
      sort:   'recent', // 'recent' | 'popular' | 'alpha'
      page:   1,

      searchTimer: null,
      pagination: { total: 0, last_page: 1, per_page: 15 },

      // Stats (total / publiés / brouillons) — mis à jour après chaque mutation
      stats: { total: 0, published: 0, drafts: 0 },

      // Modal éditeur
      modal:       { open: false, mode: 'create' },
      contentView: 'code',
      form:        { id: null, title: '', slug: '', image_url: '', status: 'draft', content: '' },
      formError:   '',
      saving:      false,

      // Modal suppression
      deleteModal: { open: false, article: null },
      deleting:    false,

      // Toast
      toast: { show: false, type: 'success', message: '' },

      // Config statique
      filters: [
        { label: 'Tous',       value: 'all',       dot: null            },
        { label: 'Publiés',    value: 'published', dot: 'bg-green-500'  },
        { label: 'Brouillons', value: 'draft',     dot: 'bg-amber-400'  },
      ],
      statusOptions: [
        { label: 'Publié',    value: 'published', icon: 'fa-solid fa-circle-check'   },
        { label: 'Brouillon', value: 'draft',     icon: 'fa-solid fa-pen-to-square'  },
      ],
      sortLabels: { popular: 'Populaires', alpha: 'A → Z' },
    }
  },

  computed: {
    // Y a-t-il au moins un filtre non-par-défaut actif ?
    hasActiveFilters() {
      return this.search !== '' || this.filter !== 'all' || this.sort !== 'recent'
    },

    // Badges compteurs dans les tabs
    // Retourne la valeur des stats pour les onglets published/draft, null pour 'all'
    filterCount() {
      return (value) => {
        if (value === 'all')       return this.stats.total       || null
        if (value === 'published') return this.stats.published   || null
        if (value === 'draft')     return this.stats.drafts      || null
        return null
      }
    },

    pages() {
      const total = this.pagination.last_page
      const cur   = this.page
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const arr = [1]
      if (cur > 3) arr.push('...')
      for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) arr.push(i)
      if (cur < total - 2) arr.push('...')
      arr.push(total)
      return arr
    },

    wordCount() {
      const text = new DOMParser().parseFromString(this.form.content, 'text/html').body.textContent || ''
      return text.trim().split(/\s+/).filter(Boolean).length
    },

    readTimeEstimate() {
      return Math.max(1, Math.ceil(this.wordCount / 200))
    },
  },

  mounted() {
    this.fetch()
    this.fetchStats()
  },

  methods: {
    // ─────────────────────────────────────────────────────────
    // DONNÉES
    // ─────────────────────────────────────────────────────────

    async fetch() {
      this.loading = true
      const response = await this.$request.post('/admin/blog/list', {
        page:     this.page,
        per_page: 15,
        sort:     this.sort,
        admin:    1,
        ...(this.search           && { search: this.search }),
        ...(this.filter !== 'all' && { status: this.filter }),
      })
      this.articles   = (response.data || []).map(a => ({ ...a, _toggling: false }))
      this.pagination = {
        total:     response.total,
        last_page: response.last_page,
        per_page:  response.per_page,
      }
      this.loading = false
    },

    // Stats globales indépendantes des filtres courants
    async fetchStats() {
      const response = await this.$request.get('/admin/blog/stats')
      this.stats = {
        total:     response.total     || 0,
        published: response.published || 0,
        drafts:    response.drafts    || 0,
      }
    },

    // ─────────────────────────────────────────────────────────
    // FILTRES
    // ─────────────────────────────────────────────────────────

    // Règle d'or : tout changement de filtre remet page à 1 puis relance fetch()

    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.page = 1
        this.fetch()
      }, 380)
    },

    setFilter(value) {
      if (this.filter === value) return  // pas de re-fetch si déjà actif
      this.filter = value
      this.page   = 1
      this.fetch()
    },

    onSortChange() {
      this.page = 1
      this.fetch()
    },

    clearSearch() {
      this.search = ''
      this.page   = 1
      this.fetch()
    },

    clearSort() {
      this.sort = 'recent'
      this.page = 1
      this.fetch()
    },

    resetFilters() {
      this.search = ''
      this.filter = 'all'
      this.sort   = 'recent'
      this.page   = 1
      this.fetch()
    },

    goTo(p) {
      if (p < 1 || p > this.pagination.last_page) return
      this.page = p
      this.fetch()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    // ─────────────────────────────────────────────────────────
    // TOGGLE RAPIDE
    // ─────────────────────────────────────────────────────────

    async quickToggle(article) {
      const newStatus   = article.status === 'published' ? 'draft' : 'published'
      const prev        = article.status
      article._toggling = true
      article.status    = newStatus  // optimistic update
      try {
        await this.$request.post(`/admin/blog/update/${article.id}`, { _method: 'PUT', status: newStatus })
        this.showToast(
          newStatus === 'published' ? '⚡ Article publié !' : 'Article passé en brouillon.',
          'success',
        )
        // Si on filtre par statut, l'article n'appartient plus à la liste courante → re-fetch
        if (this.filter !== 'all') this.fetch()
        this.fetchStats()
      } catch {
        article.status    = prev  // rollback
        article._toggling = false
        this.showToast('Erreur lors du changement de statut.', 'error')
      } finally {
        article._toggling = false
      }
    },

    // ─────────────────────────────────────────────────────────
    // CRUD MODAL
    // ─────────────────────────────────────────────────────────

    openCreate() {
      this.form        = { id: null, title: '', slug: '', image_url: '', status: 'draft', content: '' }
      this.formError   = ''
      this.contentView = 'code'
      this.modal       = { open: true, mode: 'create' }
    },

    openEdit(article) {
      this.form = {
        id:        article.id,
        title:     article.title,
        slug:      article.slug,
        image_url: article.image_url || '',
        status:    article.status,
        content:   article.content,
      }
      this.formError   = ''
      this.contentView = 'code'
      this.modal       = { open: true, mode: 'edit' }
    },

    closeModal() { this.modal.open = false },

    generateSlug() {
      if (this.modal.mode !== 'create') return
      this.form.slug = this.form.title
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '').trim()
        .replace(/\s+/g, '-').replace(/-+/g, '-')
        .slice(0, 80)
    },

    async save() {
      this.formError = ''
      if (!this.form.title.trim())   { this.formError = 'Le titre est obligatoire.';   return }
      if (!this.form.content.trim()) { this.formError = 'Le contenu est obligatoire.'; return }

      this.saving = true
      try {
        if (this.modal.mode === 'create') {
          await this.$request.post('/admin/blog/create', {
            title:     this.form.title,
            slug:      this.form.slug      || undefined,
            image_url: this.form.image_url || undefined,
            status:    this.form.status,
            content:   this.form.content,
          })
          this.showToast('Article créé avec succès !')
        } else {
          await this.$request.post(`/admin/blog/update/${this.form.id}`, {
            _method:   'PUT',
            title:     this.form.title,
            image_url: this.form.image_url || undefined,
            status:    this.form.status,
            content:   this.form.content,
          })
          this.showToast('Article mis à jour !')
        }
        this.closeModal()
        this.fetch()
        this.fetchStats()
      } catch (e) {
        this.formError = e?.message || 'Une erreur est survenue.'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(article) {
      this.deleteModal = { open: true, article }
    },

    async deleteArticle() {
      this.deleting = true
      try {
        await this.$request.post(`/admin/blog/delete/${this.deleteModal.article.id}`, { _method: 'DELETE' })
        this.deleteModal.open = false
        this.showToast('Article supprimé.')
        // Si on vide la dernière page, reculer d'une page
        if (this.articles.length === 1 && this.page > 1) this.page--
        this.fetch()
        this.fetchStats()
      } catch {
        this.showToast('Erreur lors de la suppression.', 'error')
      } finally {
        this.deleting = false
      }
    },

    // ─────────────────────────────────────────────────────────
    // HELPERS
    // ─────────────────────────────────────────────────────────

    showToast(message, type = 'success') {
      this.toast = { show: true, type, message }
      setTimeout(() => this.toast.show = false, 3500)
    },

    formatDate(d) {
      return d
        ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—'
    },
  },
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: all .22s ease }
.modal-enter-from, .modal-leave-to       { opacity: 0 }
.toast-enter-active, .toast-leave-active { transition: all .28s ease }
.toast-enter-from, .toast-leave-to       { opacity: 0; transform: translateY(10px) }
</style>

<style scoped>
.blog-content { font-size:1rem; line-height:1.8; color:#374151 }
.blog-content h2 { font-size:1.5rem; font-weight:900; margin-top:2.5rem; margin-bottom:.85rem; padding-bottom:.5rem; border-bottom:3px solid #fed7aa }
.blog-content h3 { font-size:1.15rem; font-weight:800; margin-top:1.75rem; margin-bottom:.65rem }
.blog-content p  { margin-bottom:1.25rem }
.blog-content strong { font-weight:800; color:#111827 }
.blog-content a  { color:#f97316; font-weight:700; text-decoration:underline; text-decoration-color:#fed7aa }
.blog-content ul { list-style:none; padding:0; margin-bottom:1.25rem }
.blog-content ul li { padding-left:1.5rem; position:relative; margin-bottom:.5rem; color:#4b5563 }
.blog-content ul li::before { content:''; position:absolute; left:0; top:.65em; width:7px; height:7px; background:#f97316; border-radius:50% }
.blog-content ol { list-style:decimal; padding-left:1.5rem; margin-bottom:1.25rem }
.blog-content ol li { margin-bottom:.4rem; color:#4b5563 }
.blog-content blockquote { border-left:4px solid #f97316; background:#fff7ed; padding:.85rem 1.25rem; border-radius:0 .75rem .75rem 0; margin:1.75rem 0; font-style:italic; color:#92400e }
</style>